import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Perfil.css';
import { User, ShoppingBag, MapPin, LogOut, Edit2, Save, X, Package, Clock, CheckCircle, XCircle, Bike, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import {
  getUserOrders,
  cancelOrder,
  createOrderReview,
  PAYMENT_METHOD_LABELS
} from '../services/orders';
import { getRouteDirections, geocodeAddress, reverseGeocode } from '../services/routes';
import { backendGet, backendPut } from '../lib/backendClient';
import { resolveStoreLogo } from '@/lib/storeImages';
import TrackingMap from '@/components/TrackingMap';

const USER_ORDERS_POLL_VISIBLE_MS = 15000;
const USER_ORDERS_POLL_HIDDEN_MS = 60000;

function parseCoord(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

export default function Perfil() {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    email: user?.email || ''
  });
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: 'Casa',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: '',
    lat: null,
    lng: null,
  });
  const [addressLocating, setAddressLocating] = useState(false);
  const [addressReverseLoading, setAddressReverseLoading] = useState(false);
  const [addressReverseError, setAddressReverseError] = useState('');
  const [addressAutoLabel, setAddressAutoLabel] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reviewOrderId, setReviewOrderId] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTargetOrderId, setCancelTargetOrderId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [orderRouteData, setOrderRouteData] = useState(null);
  const [orderRouteLoading, setOrderRouteLoading] = useState(false);
  const [orderRouteError, setOrderRouteError] = useState('');
  const [resolvedCustomerCoords, setResolvedCustomerCoords] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const ordersLoadingRef = useRef(false);


  useEffect(() => {
    const tabFromQuery = new URLSearchParams(location.search).get('tab');
    if (tabFromQuery === 'address' || tabFromQuery === 'orders' || tabFromQuery === 'profile') {
      setActiveTab(tabFromQuery);
    }
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (user && isMounted) {
        await loadUserProfile(isMounted);
        await loadUserOrders(isMounted);
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user || activeTab !== 'orders') return undefined;

    let stopped = false;
    let timeoutId = null;

    const schedule = () => {
      if (stopped) return;
      const delay = isDocumentVisible() ? USER_ORDERS_POLL_VISIBLE_MS : USER_ORDERS_POLL_HIDDEN_MS;
      timeoutId = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;
      if (!isDocumentVisible()) {
        schedule();
        return;
      }
      await loadUserOrders(true);
      schedule();
    };

    schedule();

    return () => {
      stopped = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [user, activeTab]);

  useEffect(() => {
    if (!Array.isArray(orders) || orders.length === 0) {
      setSelectedOrder(null);
      return;
    }

    setSelectedOrder((prev) => {
      if (!prev) return orders[0];
      return orders.find((order) => order.id === prev.id) || orders[0];
    });
  }, [orders]);

  useEffect(() => {
    const currentOrderId = selectedOrder?.id || null;
    if (!currentOrderId || currentOrderId === reviewOrderId) return;
    setReviewOrderId(currentOrderId);
    setReviewRating(0);
    setReviewComment('');
    setReviewSubmitting(false);
  }, [selectedOrder?.id, reviewOrderId]);

  async function loadUserProfile(isMounted = true) {
    try {
      const data = await backendGet('/api/auth/profile');

      if (!isMounted) return;

      setProfile({
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || user.email
      });

      const normalizedAddresses = normalizePrimaryAddresses(
        Array.isArray(data.addresses)
          ? data.addresses
          : (data.address ? [data.address] : [])
      );

      setAddresses(normalizedAddresses);

      if (normalizedAddresses.length > 0) {
        const first = normalizedAddresses.find((addr) => addr.is_primary) || normalizedAddresses[0];
        setAddressForm({
          label: first.label || 'Casa',
          street: first.street || '',
          number: first.number || '',
          complement: first.complement || '',
          neighborhood: first.neighborhood || '',
          city: first.city || '',
          state: first.state || '',
          zipcode: first.zipcode || '',
          lat: parseCoord(first.lat),
          lng: parseCoord(first.lng),
        });
      }
    } catch (err) {
      if (isMounted) {
        if (err?.statusCode !== 401) {
          console.error('Erro ao carregar perfil:', err);
        }
      }
    }
  }

  async function loadUserOrders(isMounted = true) {
    if (!isMounted || ordersLoadingRef.current) return;

    ordersLoadingRef.current = true;
    setLoadingOrders(true);
    try {
      const data = await getUserOrders(user.id);

      if (!isMounted) return;

      setOrders(data || []);
    } catch (err) {
      if (!isMounted) return;
      if (err?.statusCode === 401) return;

      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel carregar seus pedidos.',
      });
    } finally {
      ordersLoadingRef.current = false;
      if (isMounted) {
        setLoadingOrders(false);
      }
    }
  }

  async function handleSaveProfile() {
    try {
      await backendPut('/api/auth/profile', {
        name: profile.name,
        phone: profile.phone
      });

      toast({
        title: 'Sucesso!',
        description: 'Perfil atualizado com sucesso.',
      });
      setEditingProfile(false);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel atualizar o perfil.',
      });
    }
  }

  function handleAddressChange(field, value) {
    setAddressForm((prev) => ({ ...prev, [field]: value }));
  }

  function normalizePrimaryAddresses(list, preferredIndex = null) {
    const safeList = Array.isArray(list)
      ? list.filter((item) => item && typeof item === 'object')
      : [];

    if (safeList.length === 0) return [];

    let targetPrimaryIndex = safeList.findIndex((item) => item.is_primary === true);
    if (preferredIndex !== null && preferredIndex >= 0 && preferredIndex < safeList.length) {
      targetPrimaryIndex = preferredIndex;
    }
    if (targetPrimaryIndex < 0) targetPrimaryIndex = 0;

    return safeList.map((item, idx) => ({
      ...item,
      is_primary: idx === targetPrimaryIndex,
    }));
  }

  function startAddressEdit(index = null) {
    const current = index !== null ? (addresses[index] || {}) : {};

    setEditingAddressIndex(index);
    setAddressReverseError('');
    setAddressAutoLabel('');
    setAddressForm({
      label: current.label || 'Casa',
      street: current.street || '',
      number: current.number || '',
      complement: current.complement || '',
      neighborhood: current.neighborhood || '',
      city: current.city || '',
      state: current.state || '',
      zipcode: current.zipcode || '',
      lat: parseCoord(current.lat),
      lng: parseCoord(current.lng),
    });
    setEditingAddress(true);
  }

  function handleCancelAddressEdit() {
    setEditingAddress(false);
    setEditingAddressIndex(null);
    setAddressReverseError('');
    setAddressAutoLabel('');
    setAddressReverseLoading(false);
    setAddressLocating(false);
  }

  async function handleSaveAddress() {
    const normalizedLat = parseCoord(addressForm.lat);
    const normalizedLng = parseCoord(addressForm.lng);
    const payload = {
      label: (addressForm.label || 'Casa').trim() || 'Casa',
      street: (addressForm.street || '').trim(),
      number: (addressForm.number || '').trim(),
      complement: (addressForm.complement || '').trim(),
      neighborhood: (addressForm.neighborhood || '').trim(),
      city: (addressForm.city || '').trim(),
      state: (addressForm.state || '').trim().toUpperCase(),
      zipcode: (addressForm.zipcode || '').trim(),
      lat: normalizedLat,
      lng: normalizedLng,
      is_primary: editingAddressIndex === null ? addresses.length === 0 : Boolean(addresses[editingAddressIndex]?.is_primary),
    };

    if (!payload.street || !payload.number || !payload.neighborhood || !payload.city || !payload.state || !payload.zipcode) {
      toast({
        variant: 'destructive',
        title: 'Endereco incompleto',
        description: 'Preencha os campos obrigatorios do endereco.',
      });
      return;
    }

    const nextAddresses = [...addresses];
    if (editingAddressIndex === null) {
      nextAddresses.push(payload);
    } else {
      nextAddresses[editingAddressIndex] = payload;
    }

    const normalizedToSave = normalizePrimaryAddresses(nextAddresses);

    try {
      await backendPut('/api/auth/profile', {
        addresses: normalizedToSave,
        address: (normalizedToSave.find((addr) => addr.is_primary) || normalizedToSave[0] || null),
      });
      setAddresses(normalizedToSave);
      setEditingAddress(false);
      setEditingAddressIndex(null);
      toast({
        title: 'Endereco salvo',
        description: 'Seu endereco foi atualizado com sucesso.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel salvar o endereco.',
      });
    }
  }

  async function setPrimaryAddress(index) {
    const normalizedToSave = normalizePrimaryAddresses(addresses, index);

    try {
      await backendPut('/api/auth/profile', {
        addresses: normalizedToSave,
        address: normalizedToSave[index] || normalizedToSave[0] || null,
      });
      setAddresses(normalizedToSave);
      toast({
        title: 'Endereco principal atualizado',
        description: 'O endereco selecionado sera usado automaticamente no checkout.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel definir o endereco principal.',
      });
    }
  }

  function handleCancelOrder(orderId) {
    setCancelTargetOrderId(orderId);
    setCancelModalOpen(true);
  }

  function closeCancelModal() {
    if (cancelLoading) return;
    setCancelModalOpen(false);
    setCancelTargetOrderId(null);
  }

  async function handleConfirmCancelOrder() {
    if (!cancelTargetOrderId) return;

    setCancelLoading(true);
    try {
      await cancelOrder(
        cancelTargetOrderId,
        user.id,
        'outro_motivo',
        'Cancelado pelo cliente'
      );

      const nowIso = new Date().toISOString();
      setOrders((prev) => (
        Array.isArray(prev)
          ? prev.map((order) => (
            order.id === cancelTargetOrderId
              ? {
                  ...order,
                  status: 'cancelled',
                  updated_at: nowIso,
                  cancellation_reason: 'outro_motivo',
                  cancellation_comment: 'Cancelado pelo cliente',
                  cancelled_by: 'customer',
                }
              : order
          ))
          : prev
      ));

      setSelectedOrder((prev) => (
        prev && prev.id === cancelTargetOrderId
          ? {
              ...prev,
              status: 'cancelled',
              updated_at: nowIso,
              cancellation_reason: 'outro_motivo',
              cancellation_comment: 'Cancelado pelo cliente',
              cancelled_by: 'customer',
            }
          : prev
      ));

      toast({
        title: 'Pedido cancelado',
        description: 'Seu pedido foi cancelado com sucesso.',
      });

      setCancelModalOpen(false);
      setCancelTargetOrderId(null);
      await loadUserOrders(true);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err.message || 'Nao foi possivel cancelar o pedido.',
      });
    } finally {
      setCancelLoading(false);
    }
  }

  async function handleSubmitStoreReview() {
    if (!selectedOrder?.id) return;
    const ratingValue = Number(reviewRating);
    if (!Number.isFinite(ratingValue) || ratingValue < 1 || ratingValue > 5) {
      toast({
        variant: 'destructive',
        title: 'Nota obrigatoria',
        description: 'Escolha uma nota de 1 a 5 estrelas para enviar a avaliacao.',
      });
      return;
    }

    setReviewSubmitting(true);
    try {
      const response = await createOrderReview(selectedOrder.id, {
        rating: ratingValue,
        comment: reviewComment,
      });
      const reviewPayload = response?.review || {
        rating: ratingValue,
        comment: (reviewComment || '').trim() || null,
        created_at: new Date().toISOString(),
      };

      setOrders((prev) => (
        Array.isArray(prev)
          ? prev.map((order) => (
            order.id === selectedOrder.id
              ? { ...order, review: reviewPayload, can_review_store: false }
              : order
          ))
          : prev
      ));

      setSelectedOrder((prev) => (
        prev && prev.id === selectedOrder.id
          ? { ...prev, review: reviewPayload, can_review_store: false }
          : prev
      ));

      toast({
        title: 'Avaliacao enviada',
        description: 'Obrigado por avaliar esta loja.',
      });

      setReviewComment('');
      await loadUserOrders(true);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao avaliar',
        description: err?.message || 'Nao foi possivel enviar sua avaliacao.',
      });
    } finally {
      setReviewSubmitting(false);
    }
  }

  async function reverseGeocodeAddressForm(lat, lng) {
    const roundedLat = Number(lat.toFixed(6));
    const roundedLng = Number(lng.toFixed(6));

    setAddressReverseLoading(true);
    setAddressReverseError('');

    try {
      const data = await reverseGeocode({ lat: roundedLat, lng: roundedLng });
      if (!data) return;

      setAddressAutoLabel(data.label || '');
      setAddressForm((prev) => ({
        ...prev,
        lat: roundedLat,
        lng: roundedLng,
        street: data.street || prev.street,
        number: data.number || prev.number,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: (data.state_code && data.state_code.length <= 3 ? data.state_code : data.state) || prev.state,
        zipcode: data.zipcode || prev.zipcode,
      }));
    } catch (err) {
      setAddressReverseError(err?.message || 'Nao foi possivel preencher o endereco automaticamente.');
    } finally {
      setAddressReverseLoading(false);
    }
  }

  function handleUseCurrentLocationForAddress() {
    if (!navigator.geolocation) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Geolocalizacao nao suportada neste dispositivo.',
      });
      return;
    }

    setAddressLocating(true);
    setAddressReverseError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = Number(position?.coords?.latitude);
        const lng = Number(position?.coords?.longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setAddressLocating(false);
          setAddressReverseError('Coordenadas invalidas retornadas pelo navegador.');
          return;
        }

        setAddressForm((prev) => ({ ...prev, lat, lng }));
        await reverseGeocodeAddressForm(lat, lng);
        setAddressLocating(false);
      },
      () => {
        setAddressLocating(false);
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Nao foi possivel obter sua localizacao atual.',
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      }
    );
  }

  function formatCurrency(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0,00';
    return num.toFixed(2).replace('.', ',');
  }

  function getDisplayStatus(order) {
    const status = order?.status;
    if (!order) return status;

    const fulfillmentType = (order.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType === 'delivery') {
      if (status === 'pending') return 'pending';
      if (status === 'confirmed') return 'confirmed';
      if (status === 'preparing' || status === 'ready' || status === 'awaiting_pickup') return 'preparing';
      if (status === 'out_for_delivery') return 'out_for_delivery';
      return status;
    }

    return status;
  }

  function normalizePaymentMethod(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizePaymentStatus(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isOnlinePaymentMethod(value) {
    return ['pix', 'credit_card', 'debit_card', 'card', 'voucher'].includes(normalizePaymentMethod(value));
  }

  function isAwaitingPaymentApproval(order) {
    if (!order || typeof order !== 'object') return false;
    const status = String(order.status || '').trim().toLowerCase();
    if (status !== 'pending') return false;
    if (!isOnlinePaymentMethod(order.payment_method)) return false;
    return normalizePaymentStatus(order.payment_status) !== 'approved';
  }

  function hasStoreReview(order) {
    const rating = Number(order?.review?.rating);
    return Number.isFinite(rating) && rating >= 1 && rating <= 5;
  }

  function canReviewStore(order) {
    if (!order) return false;
    if (typeof order.can_review_store === 'boolean') {
      return order.can_review_store;
    }
    return String(order.status || '').toLowerCase() === 'delivered' && !hasStoreReview(order);
  }

  function getPickupConfirmationCode(order) {
    return String(order?.pickup_confirmation?.code || '').trim();
  }

  function shouldShowPickupConfirmationCode(order) {
    if (!order) return false;
    const fulfillmentType = (order.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType !== 'pickup') return false;
    const status = String(order.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'delivered') return false;
    return Boolean(getPickupConfirmationCode(order));
  }

  function getDeliveryConfirmationCode(order) {
    return String(order?.delivery_confirmation?.code || '').trim();
  }

  function shouldShowDeliveryConfirmationCode(order) {
    if (!order) return false;
    const fulfillmentType = (order.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType !== 'delivery') return false;
    const status = String(order.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'delivered') return false;
    return Boolean(getDeliveryConfirmationCode(order));
  }

  async function copyCodeToClipboard(code, successDescription) {
    if (!code) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement('input');
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }

      toast({
        title: 'Codigo copiado',
        description: successDescription,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Nao foi possivel copiar',
        description: 'Copie manualmente o codigo exibido.',
      });
    }
  }

  async function handleCopyDeliveryCode(order) {
    const code = getDeliveryConfirmationCode(order);
    await copyCodeToClipboard(code, 'Mostre este codigo ao motoboy para finalizar a corrida.');
  }

  async function handleCopyPickupCode(order) {
    const code = getPickupConfirmationCode(order);
    await copyCodeToClipboard(code, 'Mostre este codigo ao lojista para confirmar a retirada.');
  }

  function getCustomerStatusLabel(order) {
    if (isAwaitingPaymentApproval(order)) {
      return 'Aguardando confirmacao de pagamento';
    }

    const displayStatus = getDisplayStatus(order);
    const fulfillmentType = (order?.fulfillment_type || 'delivery').toLowerCase();

    if (fulfillmentType === 'delivery') {
      const labels = {
        pending: 'Aguardando confirmacao',
        confirmed: 'Pedido confirmado',
        preparing: 'Preparando o pedido',
        out_for_delivery: 'Seu pedido esta a caminho de voce',
        delivered: 'Entregue',
        cancelled: 'Cancelado',
      };
      return labels[displayStatus] || 'Aguardando confirmacao';
    }

    const pickupLabels = {
      pending: 'Aguardando confirmacao',
      confirmed: 'Confirmado',
      preparing: 'Preparando',
      ready: 'Pronto para retirada',
      awaiting_pickup: 'Aguardando retirada',
      out_for_delivery: 'Saiu para entrega',
      delivered: 'Entregue',
      cancelled: 'Cancelado',
    };

    return pickupLabels[displayStatus] || displayStatus || '-';
  }

  function getStatusIcon(status) {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <XCircle className="status-icon cancelled" />;
      case 'out_for_delivery':
        return <Package className="status-icon in-transit" />;
      default:
        return <Clock className="status-icon pending" />;
    }
  }

  function getStatusClass(status) {
    switch (status) {
      case 'delivered':
        return 'status-delivered';
      case 'cancelled':
        return 'status-cancelled';
      case 'out_for_delivery':
        return 'status-in-transit';
      case 'preparing':
        return 'status-preparing';
      default:
        return 'status-pending';
    }
  }

  function formatOrderDateTime(value, fallback = null) {
    const raw = value || fallback;
    if (!raw) return '-';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function normalizeProductName(value) {
    const raw = String(value || '').replace(/\s+/g, ' ').trim();
    if (!raw) return '-';
    const isSpacedSingleChars = /^([A-Za-zÀ-ÿ0-9]\s){2,}[A-Za-zÀ-ÿ0-9]$/.test(raw);
    if (isSpacedSingleChars) return raw.replace(/\s+/g, '');
    return raw;
  }

  function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  const rawCustomerCoords = useMemo(() => {
    const address = selectedOrder?.delivery_address || {};
    const lat = toNumber(address.lat ?? address.latitude);
    const lng = toNumber(address.lng ?? address.longitude);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [selectedOrder]);

  const restaurantCoords = useMemo(() => {
    const restaurant = selectedOrder?.restaurant || {};
    const address = selectedOrder?.delivery_address || {};
    const lat = toNumber(restaurant.lat ?? address.restaurant_lat);
    const lng = toNumber(restaurant.lng ?? address.restaurant_lng);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [selectedOrder]);

  const selectedOrderAddressCard = useMemo(() => {
    if (!selectedOrder) {
      return {
        isPickup: false,
        line1: '-',
        line2: '-',
        zip: '-',
      };
    }

    const address = selectedOrder?.delivery_address || {};
    const fulfillmentType = (selectedOrder?.fulfillment_type || 'delivery').toLowerCase();
    const isPickup = fulfillmentType === 'pickup' || address.type === 'pickup';

    if (isPickup) {
      const restaurantName = String(selectedOrder?.restaurant?.name || '').trim();
      const restaurantAddress = String(selectedOrder?.restaurant?.address || '').trim();
      const fallbackStreet = String(address.street || '').trim();
      const fallbackNumber = String(address.number || '').trim();
      const fallbackAddress = [fallbackStreet, fallbackNumber].filter(Boolean).join(', ');
      const line1 = restaurantAddress || fallbackAddress || 'Endereco da loja nao informado';
      const line2 = restaurantName ? `Retirada na loja: ${restaurantName}` : 'Retirada na loja';
      const zip = String(address.zipcode || '').trim() || '-';
      return { isPickup, line1, line2, zip };
    }

    const street = String(address.street || '').trim();
    const number = String(address.number || '').trim();
    const complement = String(address.complement || '').trim();
    const neighborhood = String(address.neighborhood || '').trim();
    const city = String(address.city || '').trim();
    const state = String(address.state || '').trim();
    const line1Base = [street, number].filter(Boolean).join(', ');
    const line1 = line1Base ? `${line1Base}${complement ? ` - ${complement}` : ''}` : '-';
    const cityState = [city, state].filter(Boolean).join(' - ');
    const line2 = [neighborhood, cityState].filter(Boolean).join(', ') || '-';
    const zip = String(address.zipcode || '').trim() || '-';
    return { isPickup, line1, line2, zip };
  }, [selectedOrder]);

  useEffect(() => {
    if (!selectedOrder) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    if (rawCustomerCoords) {
      setResolvedCustomerCoords(rawCustomerCoords);
      setGeocodeError('');
      return;
    }

    const address = selectedOrder?.delivery_address || {};
    const fulfillmentType = (selectedOrder?.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType === 'pickup' || address.type === 'pickup') {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    const text = [
      address.label,
      address.address,
      address.full_address,
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state,
      address.zipcode,
    ]
      .filter(Boolean)
      .map((v) => String(v).trim())
      .filter(Boolean)
      .join(', ');

    if (!text) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    let cancelled = false;
    setGeocodeLoading(true);
    setGeocodeError('');

    geocodeAddress({ text })
      .then((data) => {
        if (cancelled) return;
        const lat = Number(data?.lat);
        const lng = Number(data?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setResolvedCustomerCoords({ lat, lng });
          return;
        }
        setResolvedCustomerCoords(null);
        setGeocodeError('Nao foi possivel localizar o endereco');
      })
      .catch((err) => {
        if (cancelled) return;
        setResolvedCustomerCoords(null);
        setGeocodeError(err?.message || 'Nao foi possivel localizar o endereco');
      })
      .finally(() => {
        if (!cancelled) setGeocodeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOrder, rawCustomerCoords]);

  useEffect(() => {
    const fulfillmentType = (selectedOrder?.fulfillment_type || 'delivery').toLowerCase();

    if (!selectedOrder || fulfillmentType === 'pickup' || !restaurantCoords || !resolvedCustomerCoords) {
      setOrderRouteData(null);
      setOrderRouteError('');
      return;
    }

    let cancelled = false;
    setOrderRouteLoading(true);
    setOrderRouteError('');

    getRouteDirections({ start: restaurantCoords, end: resolvedCustomerCoords })
      .then((data) => {
        if (cancelled) return;
        setOrderRouteData(data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrderRouteData(null);
        setOrderRouteError(err?.message || 'Nao foi possivel carregar a rota');
      })
      .finally(() => {
        if (!cancelled) setOrderRouteLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOrder?.id, selectedOrder?.fulfillment_type, restaurantCoords, resolvedCustomerCoords]);

  return (
    <div className="perfil-page">
      <div className="perfil-container">
        <aside className="perfil-sidebar">
          <div className="sidebar-header">
            <div className="user-avatar">
              {profile.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <h2 className="perfil-title">Minha Conta</h2>
            <p className="user-email">{user?.email}</p>
          </div>

          <nav className="perfil-nav">
            <button
              className={`perfil-menu ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={20} />
              Perfil
            </button>

            <button
              className={`perfil-menu ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <ShoppingBag size={20} />
              Meus pedidos
              {orders.length > 0 && <span className="badge">{orders.length}</span>}
            </button>

            <button
              className={`perfil-menu ${activeTab === 'address' ? 'active' : ''}`}
              onClick={() => setActiveTab('address')}
            >
              <MapPin size={20} />
              Endereços
            </button>

            <button
              className="perfil-menu perfil-menu-service"
              onClick={() => navigate('/perfil/envios-viagens')}
            >
              <Bike size={20} />
              Envios e Viagens
            </button>

            <button className="perfil-menu logout" onClick={signOut}>
              <LogOut size={20} />
              Sair
            </button>
          </nav>

          <button className="btn-home" onClick={() => navigate('/')}>
            Voltar para Home
          </button>
        </aside>

        <main className="perfil-content">
          {activeTab === 'profile' && (
            <section className="content-section profile-overview-section">
              <div className="section-header perfil-primary-header">
                <div className="perfil-heading-block">
                  <span className="perfil-kicker">Area da conta</span>
                  <h1>Meu Perfil</h1>
                  <p className="perfil-heading-subtitle">Mantenha seus dados pessoais atualizados para uma experiencia mais fluida.</p>
                </div>
                {!editingProfile ? (
                  <button className="btn-edit" onClick={() => setEditingProfile(true)}>
                    <Edit2 size={18} />
                    Editar
                  </button>
                ) : (
                  <div className="edit-actions">
                    <button className="btn-save" onClick={handleSaveProfile}>
                      <Save size={18} />
                      Salvar
                    </button>
                    <button className="btn-cancel" onClick={() => setEditingProfile(false)}>
                      <X size={18} />
                      Cancelar
                    </button>
                  </div>
                )}
              </div>

              <div className="profile-card profile-overview-card">
                <div className="form-group">
                  <label>Nome completo</label>
                  {editingProfile ? (
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      placeholder="Digite seu nome"
                    />
                  ) : (
                    <p className="profile-value">{profile.name || 'Nao informado'}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>E-mail</label>
                  <p className="profile-value">{profile.email}</p>
                  <small className="help-text">O e-mail nao pode ser alterado</small>
                </div>

                <div className="form-group">
                  <label>Telefone</label>
                  {editingProfile ? (
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  ) : (
                    <p className="profile-value">{profile.phone || 'Nao informado'}</p>
                  )}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'orders' && (
            <section className="content-section">
              <div className="section-header">
                <h1>Meus Pedidos</h1>
              </div>

              {loadingOrders ? (
                <div className="loading-state">
                  <p>Carregando pedidos...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="empty-state">
                  <Package size={64} className="empty-icon" />
                  <h3>Nenhum pedido ainda</h3>
                  <p>Explore restaurantes e faca seu primeiro pedido!</p>
                  <button className="btn-primary" onClick={() => navigate('/')}>
                    Ver Restaurantes
                  </button>
                </div>
              ) : (
                <div className={`perfil-orders-layout ${selectedOrder ? 'has-selected-detail' : ''}`}>
                  <div className="orders-list perfil-orders-list-pane">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className={`order-card ${selectedOrder?.id === order.id ? 'selected-order-card' : ''}`}
                        onClick={() => setSelectedOrder(order)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedOrder(order);
                          }
                        }}
                      >
                        <div className="order-header">
                          <div className="order-info">
                            <div className="order-restaurant">
                              <img src={resolveStoreLogo(order.restaurant)} alt={order.restaurant?.name || 'Restaurante'} />
                              <div>
                                <h3>{order.restaurant?.name || 'Restaurante'}</h3>
                                <p className="order-number">Pedido #{order.order_number}</p>
                              </div>
                            </div>
                            <div className={`order-status ${getStatusClass(getDisplayStatus(order))}`}>
                              {getStatusIcon(getDisplayStatus(order))}
                              <span>{getCustomerStatusLabel(order)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="order-body">
                          <div className="order-items">
                            {order.order_items?.map((item) => (
                              <div key={item.id} className="order-item">
                                <span className="item-quantity">{item.quantity}x</span>
                                <span className="item-name">{normalizeProductName(item.product_name)}</span>
                                <span className="item-price">
                                  R$ {formatCurrency(item.subtotal)}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="order-footer">
                            <div className="order-details">
                              <p className="order-date">{formatOrderDateTime(order.created_at)}</p>
                              <p className="order-payment">
                                {PAYMENT_METHOD_LABELS[order.payment_method]}
                              </p>
                            </div>
                            <div className="order-total">
                              <span>Total</span>
                              <strong>R$ {formatCurrency(order.total)}</strong>
                            </div>
                          </div>

                          {canReviewStore(order) && (
                            <button
                              className="btn-review-order"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(order);
                              }}
                            >
                              Avaliar loja
                            </button>
                          )}

                          {(order.status === 'pending' || order.status === 'confirmed') && (
                            <button
                              className="btn-cancel-order"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelOrder(order.id);
                              }}
                            >
                              Cancelar Pedido
                            </button>
                          )}

                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedOrder && (
                    <aside className="perfil-order-detail-panel">
                      <div className="perfil-order-detail-header">
                        <h2>Detalhes do Pedido</h2>
                        <button className="perfil-detail-close" onClick={() => setSelectedOrder(null)}>
                          X
                        </button>
                      </div>

                      <div className="perfil-order-detail-body">
                        <div className="perfil-order-detail-section">
                          <h3>Status atual</h3>
                          <div className={`perfil-status-display ${getStatusClass(getDisplayStatus(selectedOrder))}`}>
                            {getStatusIcon(getDisplayStatus(selectedOrder))}
                            <div>
                              <p className="perfil-status-label">{getCustomerStatusLabel(selectedOrder)}</p>
                              <p className="perfil-status-time">
                                Atualizado em {formatOrderDateTime(selectedOrder.updated_at, selectedOrder.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {hasStoreReview(selectedOrder) && (
                          <div className="perfil-order-detail-section review-section review-section-readonly">
                            <h3>Sua avaliacao da loja</h3>
                            <div className="review-stars-row">
                              {Array.from({ length: 5 }).map((_, index) => {
                                const value = index + 1;
                                const active = value <= Number(selectedOrder?.review?.rating || 0);
                                return (
                                  <span key={value} className={`review-star-icon ${active ? 'active' : ''}`}>
                                    <Star size={16} fill={active ? 'currentColor' : 'none'} />
                                  </span>
                                );
                              })}
                              <span className="review-score-text">
                                {Number(selectedOrder?.review?.rating || 0).toFixed(1).replace('.', ',')}
                              </span>
                            </div>
                            {selectedOrder?.review?.comment ? (
                              <p className="review-comment-text">"{selectedOrder.review.comment}"</p>
                            ) : (
                              <p className="review-hint-text">Voce avaliou sem comentario.</p>
                            )}
                            <p className="review-meta-text">
                              Enviado em {formatOrderDateTime(selectedOrder?.review?.created_at, selectedOrder?.created_at)}
                            </p>
                          </div>
                        )}

                        {canReviewStore(selectedOrder) && !hasStoreReview(selectedOrder) && (
                          <div className="perfil-order-detail-section review-section">
                            <h3>Avaliar loja</h3>
                            <p className="review-hint-text">
                              Seu pedido foi entregue. Conte como foi sua experiencia com a loja.
                            </p>
                            <div className="review-stars-row">
                              {Array.from({ length: 5 }).map((_, index) => {
                                const value = index + 1;
                                const active = value <= Number(reviewRating || 0);
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    className={`review-star-btn ${active ? 'active' : ''}`}
                                    onClick={() => setReviewRating(value)}
                                    aria-label={`Selecionar ${value} estrela(s)`}
                                  >
                                    <Star size={18} fill={active ? 'currentColor' : 'none'} />
                                  </button>
                                );
                              })}
                            </div>
                            <textarea
                              className="review-textarea"
                              value={reviewComment}
                              onChange={(event) => setReviewComment(event.target.value.slice(0, 500))}
                              rows={4}
                              placeholder="Comentario opcional sobre atendimento, qualidade e tempo."
                            />
                            <div className="review-actions-row">
                              <span className="review-char-counter">{String(reviewComment || '').length}/500</span>
                              <button
                                type="button"
                                className="review-submit-btn"
                                onClick={handleSubmitStoreReview}
                                disabled={reviewSubmitting}
                              >
                                {reviewSubmitting ? 'Enviando...' : 'Enviar avaliacao'}
                              </button>
                            </div>
                          </div>
                        )}

                        {shouldShowDeliveryConfirmationCode(selectedOrder) && (
                          <div className="perfil-order-detail-section delivery-code-section">
                            <h3>Codigo de entrega</h3>
                            <div className="delivery-code-callout">
                              <p className="delivery-code-callout-title">Informe este codigo ao motoboy.</p>
                              <p className="delivery-code-caption">O motoboy so consegue finalizar a corrida com esse codigo.</p>
                            </div>
                            <div className="delivery-code-actions">
                              <div className="delivery-code-box">{getDeliveryConfirmationCode(selectedOrder)}</div>
                              <button
                                type="button"
                                className="delivery-code-copy-btn"
                                onClick={() => handleCopyDeliveryCode(selectedOrder)}
                              >
                                Copiar codigo
                              </button>
                            </div>
                            <p className="delivery-code-helper">Mostre este codigo na entrega para concluir o pedido.</p>
                          </div>
                        )}

                        {shouldShowPickupConfirmationCode(selectedOrder) && (
                          <div className="perfil-order-detail-section delivery-code-section">
                            <h3>Codigo de retirada na loja</h3>
                            <div className="delivery-code-callout">
                              <p className="delivery-code-callout-title">Informe este codigo no balcao da loja.</p>
                              <p className="delivery-code-caption">A retirada e concluida quando o lojista confirmar este codigo.</p>
                            </div>
                            <div className="delivery-code-actions">
                              <div className="delivery-code-box">{getPickupConfirmationCode(selectedOrder)}</div>
                              <button
                                type="button"
                                className="delivery-code-copy-btn"
                                onClick={() => handleCopyPickupCode(selectedOrder)}
                              >
                                Copiar codigo
                              </button>
                            </div>
                            <p className="delivery-code-helper">Mostre este codigo ao lojista para concluir a retirada.</p>
                          </div>
                        )}

                        <div className="perfil-order-detail-section">
                          <h3>Rastreamento no mapa</h3>
                          <TrackingMap
                            restaurantLocation={restaurantCoords}
                            restaurantLabel={selectedOrder?.restaurant?.name || 'Loja'}
                            customerLocation={resolvedCustomerCoords}
                            customerLabel={profile?.name || 'Cliente'}
                            customerKind={(selectedOrder?.delivery_address?.label || '').toLowerCase().includes('trabalho') ? 'delivery_work' : 'delivery_home'}
                            route={orderRouteData?.geometry ? { type: 'Feature', geometry: orderRouteData.geometry, properties: {} } : null}
                            height={220}
                          />
                          <div className="perfil-address-display perfil-map-feedback">
                            {orderRouteLoading && <p>Carregando rota...</p>}
                            {orderRouteError && <p className="perfil-text-danger">{orderRouteError}</p>}
                            {geocodeLoading && !resolvedCustomerCoords && <p>Localizando endereco...</p>}
                            {geocodeError && <p className="perfil-text-danger">{geocodeError}</p>}
                            {selectedOrderAddressCard.isPickup && (
                              <p>Pedido para retirada na loja.</p>
                            )}
                            {!selectedOrderAddressCard.isPickup && !resolvedCustomerCoords && !geocodeLoading && (
                              <p>Endereco do cliente sem coordenadas. Atualize no checkout para habilitar rota.</p>
                            )}
                            {orderRouteData?.distance_m != null && orderRouteData?.duration_s != null && (
                              <p>
                                Distancia: {(orderRouteData.distance_m / 1000).toFixed(1).replace('.', ',')} km | Tempo: {Math.round(orderRouteData.duration_s / 60)} min
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="perfil-order-detail-section">
                          <h3>Itens do pedido</h3>
                          <div className="perfil-detail-items">
                            {selectedOrder.order_items?.map((item) => (
                              <div key={item.id} className="perfil-detail-item">
                                <div className="perfil-detail-item-info">
                                  <span className="item-quantity">{item.quantity}x</span>
                                  <span className="item-name">{normalizeProductName(item.product_name)}</span>
                                </div>
                                <span className="item-price">R$ {formatCurrency(item.subtotal)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="perfil-order-detail-section">
                          <h3>
                            <MapPin size={16} />
                            Endereco de entrega
                          </h3>
                          <div className="perfil-address-display">
                            <p>{selectedOrderAddressCard.line1}</p>
                            <p>{selectedOrderAddressCard.line2}</p>
                            <p>CEP: {selectedOrderAddressCard.zip}</p>
                          </div>
                        </div>

                        <div className="perfil-order-detail-section">
                          <h3>Resumo do pagamento</h3>
                          <div className="perfil-payment-summary">
                            <div className="perfil-payment-row">
                              <span>Subtotal</span>
                              <span>R$ {formatCurrency(selectedOrder.subtotal)}</span>
                            </div>
                            <div className="perfil-payment-row">
                              <span>Taxa de entrega</span>
                              <span>R$ {formatCurrency(selectedOrder.delivery_fee)}</span>
                            </div>
                            {Number(selectedOrder.discount) > 0 && (
                              <div className="perfil-payment-row discount">
                                <span>Desconto</span>
                                <span>- R$ {formatCurrency(selectedOrder.discount)}</span>
                              </div>
                            )}
                            <div className="perfil-payment-row total">
                              <strong>Total</strong>
                              <strong>R$ {formatCurrency(selectedOrder.total)}</strong>
                            </div>
                            <div className="perfil-payment-method">
                              Forma de pagamento: <strong>{PAYMENT_METHOD_LABELS[selectedOrder.payment_method]}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </aside>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === 'address' && (
            <section className="content-section">
              <div className="section-header">
                <h1>Endereços</h1>
                {!editingAddress ? (
                  <div className="address-header-actions">
                    <button className="btn-edit btn-add-address" onClick={() => startAddressEdit(null)}>
                      <Edit2 size={18} />
                      Adicionar Endereço
                    </button>
                  </div>
                ) : (
                  <div className="edit-actions">
                    <button className="btn-save" onClick={handleSaveAddress}>
                      <Save size={18} />
                      Salvar
                    </button>
                    <button className="btn-cancel" onClick={handleCancelAddressEdit}>
                      <X size={18} />
                      Cancelar
                    </button>
                  </div>
                )}
              </div>

              {editingAddress ? (
                <div className="profile-card">
                  <div className="form-group">
                    <label>Tipo</label>
                    <select value={addressForm.label} onChange={(e) => handleAddressChange('label', e.target.value)}>
                      <option value="Casa">Casa</option>
                      <option value="Trabalho">Trabalho</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>CEP *</label>
                    <input type="text" value={addressForm.zipcode} onChange={(e) => handleAddressChange('zipcode', e.target.value)} placeholder="00000-000" />
                  </div>
                  <div className="form-group">
                    <label>Rua *</label>
                    <input type="text" value={addressForm.street} onChange={(e) => handleAddressChange('street', e.target.value)} placeholder="Nome da rua" />
                  </div>
                  <div className="form-group">
                    <label>Numero *</label>
                    <input type="text" value={addressForm.number} onChange={(e) => handleAddressChange('number', e.target.value)} placeholder="123" />
                  </div>
                  <div className="form-group">
                    <label>Complemento</label>
                    <input type="text" value={addressForm.complement} onChange={(e) => handleAddressChange('complement', e.target.value)} placeholder="Apto, bloco, etc" />
                  </div>
                  <div className="form-group">
                    <label>Bairro *</label>
                    <input type="text" value={addressForm.neighborhood} onChange={(e) => handleAddressChange('neighborhood', e.target.value)} placeholder="Nome do bairro" />
                  </div>
                  <div className="form-group">
                    <label>Cidade *</label>
                    <input type="text" value={addressForm.city} onChange={(e) => handleAddressChange('city', e.target.value)} placeholder="Cidade" />
                  </div>
                  <div className="form-group">
                    <label>UF *</label>
                    <input type="text" value={addressForm.state} maxLength="2" onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase())} placeholder="SP" />
                  </div>
                  <div className="address-location-block">
                    <div className="address-location-header">
                      <h3>Localizacao (latitude/longitude)</h3>
                      <button
                        type="button"
                        className="address-location-btn"
                        onClick={handleUseCurrentLocationForAddress}
                        disabled={addressLocating}
                      >
                        {addressLocating ? 'Localizando...' : 'Usar localizacao do navegador'}
                      </button>
                    </div>
                    <p className="address-location-help">
                      Use esta opcao para preencher o endereco com sua localizacao atual do navegador.
                    </p>
                    <div className="address-location-grid">
                      <div className="form-group">
                        <label>Latitude</label>
                        <input
                          type="text"
                          value={addressForm.lat ?? ''}
                          onChange={(e) => handleAddressChange('lat', e.target.value)}
                          placeholder="-23.550520"
                        />
                      </div>
                      <div className="form-group">
                        <label>Longitude</label>
                        <input
                          type="text"
                          value={addressForm.lng ?? ''}
                          onChange={(e) => handleAddressChange('lng', e.target.value)}
                          placeholder="-46.633308"
                        />
                      </div>
                    </div>
                    {addressReverseLoading && <p className="address-location-help">Buscando endereco automaticamente...</p>}
                    {addressReverseError && <p className="address-location-error">{addressReverseError}</p>}
                    {addressAutoLabel && !addressReverseLoading && !addressReverseError && (
                      <p className="address-location-help">Endereco sugerido: {addressAutoLabel}</p>
                    )}
                  </div>
                </div>
              ) : addresses.length === 0 ? (
                <div className="empty-state">
                  <MapPin size={64} className="empty-icon" />
                  <h3>Nenhum endereço cadastrado</h3>
                  <p>Adicione um endereço para facilitar seus pedidos</p>
                  <button className="btn-primary" onClick={() => startAddressEdit(null)}>Cadastrar Endereço</button>
                </div>
              ) : (
                <div className="addresses-list">
                  {addresses.map((addr, index) => (
                    <div key={index} className="address-card">
                      <MapPin size={20} />
                      <div className="address-info">
                        <div className="address-top">
                          <div className="address-label-wrap">
                            <p className="address-label">{addr.label || `Endereço ${index + 1}`}</p>
                            {addr.is_primary && <span className="address-primary-badge">Principal</span>}
                          </div>
                          <div className="address-actions">
                            {!addr.is_primary && (
                              <button className="address-set-primary-btn" onClick={() => setPrimaryAddress(index)}>
                                Definir principal
                              </button>
                            )}
                            <button className="address-edit-btn" onClick={() => startAddressEdit(index)}>
                              <Edit2 size={14} />
                              Editar
                            </button>
                          </div>
                        </div>
                        <p className="address-street">{addr.street}, {addr.number} {addr.complement ? ('- ' + addr.complement) : ''}</p>
                        <p className="address-details">{addr.neighborhood}, {addr.city} - {addr.state}</p>
                        <p className="address-zipcode">CEP: {addr.zipcode}</p>
                        {Number.isFinite(parseCoord(addr.lat)) && Number.isFinite(parseCoord(addr.lng)) && (
                          <p className="address-zipcode">Lat/Lng: {Number(parseCoord(addr.lat)).toFixed(6)}, {Number(parseCoord(addr.lng)).toFixed(6)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  <button className="btn-primary add-address-footer-btn" onClick={() => startAddressEdit(null)}>
                    Adicionar outro Endereço
                  </button>
                </div>
              )}
            </section>
          )}
        </main>

        {cancelModalOpen && (
          <div className="perfil-cancel-modal-overlay" onClick={closeCancelModal}>
            <div className="perfil-cancel-modal" onClick={(event) => event.stopPropagation()}>
              <h3>Cancelar pedido</h3>
              <p>Tem certeza que deseja cancelar este pedido?</p>
              <div className="perfil-cancel-modal-actions">
                <button
                  type="button"
                  className="perfil-cancel-modal-back"
                  onClick={closeCancelModal}
                  disabled={cancelLoading}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="perfil-cancel-modal-confirm"
                  onClick={handleConfirmCancelOrder}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Cancelando...' : 'Confirmar cancelamento'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

